import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Briefcase, ArrowLeft, Sparkles, FileText, Cpu, Loader2, Copy, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResumeBuilder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: user?.name || '', email: user?.email || '', phone: '', target_role: '', skills: '', experience: '', education: '', ai_model: 'gpt' });
  const [resume, setResume] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true); setResume('');
    try {
      const { data } = await axios.post(`${API}/resume/generate`, {
        ...form,
        skills: form.skills.split(',').map(s => s.trim()).filter(Boolean)
      }, { withCredentials: true });
      setResume(data.resume);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate resume');
    }
    setLoading(false);
  };

  const copyResume = () => {
    navigator.clipboard.writeText(resume);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isPro = user?.membership === 'pro';

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-[#D97757] flex items-center justify-center">
            <Sparkles size={22} className="text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-3xl font-medium tracking-tight" style={{fontFamily:'Outfit'}}>AI Resume Builder</h1>
            <p className="text-[#1C2A24]/60 text-sm">Create a professional resume powered by AI</p>
          </div>
        </div>

        {!isPro ? (
          <div className="bg-white rounded-3xl border border-[#E5E0D8] p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="pro-required">
            <Sparkles size={48} className="text-[#D97757] mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="text-xl font-medium mb-2" style={{fontFamily:'Outfit'}}>Pro Feature</h2>
            <p className="text-[#1C2A24]/60 mb-6">Upgrade to Pro ($12.99/mo) to access the AI Resume Builder with your choice of GPT, Claude, or Gemini.</p>
            <Link to="/membership"><Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white px-8" data-testid="upgrade-btn">Upgrade to Pro</Button></Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {/* Form */}
            <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <form onSubmit={handleGenerate} className="space-y-4">
                <div>
                  <Label className="text-sm text-[#1C2A24]/70">AI Model</Label>
                  <Select value={form.ai_model} onValueChange={v => setForm(f => ({ ...f, ai_model: v }))}>
                    <SelectTrigger className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" data-testid="ai-model-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gpt"><div className="flex items-center gap-2"><Cpu size={14} /> GPT-5.2 (OpenAI)</div></SelectItem>
                      <SelectItem value="claude"><div className="flex items-center gap-2"><Cpu size={14} /> Claude (Anthropic)</div></SelectItem>
                      <SelectItem value="gemini"><div className="flex items-center gap-2"><Cpu size={14} /> Gemini (Google)</div></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm text-[#1C2A24]/70">Name</Label>
                    <Input data-testid="resume-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" required />
                  </div>
                  <div>
                    <Label className="text-sm text-[#1C2A24]/70">Email</Label>
                    <Input data-testid="resume-email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm text-[#1C2A24]/70">Phone</Label>
                    <Input data-testid="resume-phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm text-[#1C2A24]/70">Target Role</Label>
                    <Input data-testid="resume-role" value={form.target_role} onChange={e => setForm(f => ({ ...f, target_role: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" placeholder="e.g. Customer Service Rep" required />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-[#1C2A24]/70">Skills (comma-separated)</Label>
                  <Input data-testid="resume-skills" value={form.skills} onChange={e => setForm(f => ({ ...f, skills: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" placeholder="e.g. Customer Service, Excel, Communication" required />
                </div>
                <div>
                  <Label className="text-sm text-[#1C2A24]/70">Experience</Label>
                  <Textarea data-testid="resume-experience" value={form.experience} onChange={e => setForm(f => ({ ...f, experience: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" rows={3} placeholder="Describe your work experience..." required />
                </div>
                <div>
                  <Label className="text-sm text-[#1C2A24]/70">Education</Label>
                  <Textarea data-testid="resume-education" value={form.education} onChange={e => setForm(f => ({ ...f, education: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" rows={2} placeholder="Your education background..." required />
                </div>
                {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-2xl text-sm" data-testid="resume-error">{error}</div>}
                <Button data-testid="generate-resume-btn" type="submit" disabled={loading} className="w-full rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-12">
                  {loading ? <><Loader2 size={16} className="animate-spin mr-2" />Generating...</> : <><Sparkles size={16} className="mr-2" />Generate Resume</>}
                </Button>
              </form>
            </div>

            {/* Preview */}
            <div className="bg-white rounded-3xl border border-[#E5E0D8] shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden" data-testid="resume-preview">
              <div className="p-4 border-b border-[#E5E0D8] bg-[#F4F1E8]/30 flex items-center justify-between">
                <span className="text-sm font-medium" style={{fontFamily:'Outfit'}}>Resume Preview</span>
                {resume && (
                  <Button variant="ghost" size="sm" onClick={copyResume} className="rounded-full text-xs" data-testid="copy-resume-btn">
                    {copied ? <><CheckCircle2 size={14} className="mr-1" />Copied!</> : <><Copy size={14} className="mr-1" />Copy</>}
                  </Button>
                )}
              </div>
              <div className="p-6 max-h-[600px] overflow-y-auto">
                {resume ? (
                  <pre className="whitespace-pre-wrap text-sm text-[#1C2A24]/80 leading-relaxed font-[Manrope]">{resume}</pre>
                ) : (
                  <div className="text-center py-16 text-[#1C2A24]/30">
                    <FileText size={48} className="mx-auto mb-4" strokeWidth={1.5} />
                    <p>Your AI-generated resume will appear here</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
